"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { ChevronUp, ChevronDown } from "lucide-react";
import Link from "next/link";
import MDEditor from "@uiw/react-md-editor";
import styles from "@/styles/markdown.module.css";

interface CollapsibleDescriptionProps {
  tags?: string[];
  className?: string;
  description: string;
}

const CollapsibleDescription = ({
  tags,
  description,
  className,
}: CollapsibleDescriptionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 200;
  const shouldCollapse = description.length > maxLength;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={cn(className, "card-bg flex flex-col gap-3 p-4")}>
      {/* Tags */}
      {tags && (
        <div className="flex flex-row flex-wrap gap-0.5">
          {tags.map((tag) => (
            <Link
              key={tag}
              className={cn(
                buttonVariants({ variant: "secondary", size: "xs" }),
                "px-2",
              )}
              href={`/?query=${tag}&page=1`}
            >
              #{tag}
            </Link>
          ))}
        </div>
      )}

      <motion.div
        className="relative p-0.5"
        initial={false}
        animate={{ height: "auto" }}
        transition={{ duration: 0.1, ease: "easeInOut" }}
      >
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={isExpanded ? "expanded" : "collapsed"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "text-muted-foreground",
              !isExpanded && shouldCollapse && "line-clamp-3",
            )}
          >
            <MDEditor.Markdown
              source={description}
              className={styles.markdown}
              style={{
                backgroundColor: "transparent",
                font: "inherit",
                color: "inherit",
              }}
            />
          </motion.div>
        </AnimatePresence>

        {shouldCollapse && (
          <motion.button
            onClick={toggleExpand}
            className="mt-2 flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isExpanded ? (
              <>
                Show less
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                Show more
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </motion.button>
        )}
      </motion.div>
    </div>
  );
};

export default CollapsibleDescription;
